// <social-video-slider> — SICLIPS social videos engine (sections/siclips-social-videos.liquid).
//
// Performance contract: the server DOM ships zero video bytes and zero third-party bytes.
// Every card is a poster facade; this engine loads media on demand and tears it down when a
// card leaves the active window. Per-card lifecycle: EMPTY -> WARM (active±1, metadata only)
// -> ACTIVE (playing) -> back, driven by ONE sync() reducer — no scattered play() calls.
//
// Visibility uses an IntersectionObserver ON PURPOSE (vs the project's sticky-bar rule of
// rAF scroll watchers): the sticky rule exists because an instant scroll jump skips the
// CROSSING an IO waits for. Here the predicate is a STATE ("is the stage on screen now") —
// after any jump the IO reports the final state, and "teleported past => not visible =>
// paused" is exactly right.
//
// Mute model: memory-only (no storage — restricted WebViews can throw on storage access).
// Starts muted. Desktop: an unmute persists across gesture-driven card changes. Mobile:
// resets to muted on every card change (matches feed behavior and keeps every start
// autoplay-legal). Every play() is promise-guarded: a rejection (iOS Low-Power mode etc.)
// flips the card into a needs-gesture state with a big play button — never a retry loop.

class SocialVideoSlider extends HTMLElement {
  connectedCallback() {
    if (this._init) {
      // editor drag-move (same element re-connected): re-arm what disconnect tears down
      this._bindWinDoc();
      this._layout();
      this._sync();
      return;
    }
    this._init = true;

    this.cfg = this._readJSON('[data-svv-config]') || {};
    this.stage = this.querySelector('[data-svv-stage]');
    const els = Array.from(this.querySelectorAll('[data-svv-card]'));
    this.cards = els.map((el) => ({
      el,
      data: this._readJSON('[data-svv-data]', el) || {},
      poster: el.querySelector('[data-svv-poster]'),
      video: el.querySelector('[data-svv-video]'),
      tpl: el.querySelector('[data-svv-embed]'),
      spinner: el.querySelector('[data-svv-spinner]'),
      sound: el.querySelector('.svv-card__sound[data-svv-mute]'),
      ov: el.querySelector('[data-svv-ov]'),
      controls: el.querySelector('[data-svv-controls]'),
      bar: el.querySelector('[data-svv-bar]'),
      fill: el.querySelector('[data-svv-fill]'),
      curEl: el.querySelector('[data-svv-cur]'),
      durEl: el.querySelector('[data-svv-dur]'),
      bigplay: el.querySelector('[data-svv-bigplay]'),
      fallback: el.querySelector('[data-svv-fallback]'),
      iframe: null,
      ready: false,
      tries: 0,
      watchdog: null,
      token: 0,
      userPaused: false,
      userStarted: false,
      sought: false,
      mediaBound: false,
      pick: null,
    }));
    this.n = this.cards.length;
    if (!this.n || !this.stage) {
      // ZERO cards = every block's video failed to resolve (deleted/renamed in Admin Files, or
      // still transcoding). The shell used to keep rendering — a heading over a 780px empty box on
      // the live homepage (seen 2026-08-11 when the owner replaced the video files). Hide the whole
      // section instead; it returns by itself the moment the blocks resolve again. The editor is
      // exempt so the merchant can still see and configure the section.
      if (!(window.Shopify && window.Shopify.designMode)) {
        const sec = this.closest('.shopify-section');
        if (sec) sec.style.display = 'none';
      }
      return;
    }

    this.designMode = !!(window.Shopify && window.Shopify.designMode);
    this._designOk = false; // becomes true when the merchant selects a block in the editor
    this.rtl = (this.getAttribute('dir') || document.dir || '').toLowerCase() === 'rtl' || !!this.cfg.rtl;
    this.dirSign = this.rtl ? -1 : 1;
    this.mobileq = window.matchMedia('(max-width: 749px)');
    this.rmq = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.saveData = !!(navigator.connection && navigator.connection.saveData);

    this.active = 0;
    this.muted = true;
    this._hasGesture = false;
    this._mutedByUser = false;
    this._soundFromPlayTap = false;
    this.sectionVisible = false;
    this._moved = false;
    this._tkQueue = [];
    this._tkLoading = null;

    this._bindEl();
    this._bindWinDoc();
    this._layout();
    this.setAttribute('data-svv-ready', '');
    this._syncIcons();
    this._sync();
    this._announce();
  }

  disconnectedCallback() {
    this._unbindWinDoc();
    this._stopProgress();
    this.cards && this.cards.forEach((c) => this._teardown(c));
    this._tkQueue = [];
    this._tkLoading = null;
  }

  _readJSON(sel, scope) {
    const el = (scope || this).querySelector(sel);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  // ---------- window/document listeners: (re)bound per connect ----------
  _bindWinDoc() {
    if (this._winDocBound) return;
    this._winDocBound = true;

    // ⚠️ NOT a flat 0.35 ratio. A card is a % of the section's WIDTH and an Instagram shell card
    // is ~1.7x its own width tall, so on any narrow viewport shorter than ~0.6x its width — an
    // iPhone SE, a 360x640 Android, any phone in LANDSCAPE — 35% of the stage cannot fit on
    // screen at once (measured max 0.329 at 667x375). sectionVisible then never turned true and
    // NOTHING loaded at all. Judge on visible PIXELS against whichever is smaller, the stage or
    // the viewport, and pass a threshold LIST: with a single value the callback stops firing once
    // the stage is pinned across the viewport.
    this._io = new IntersectionObserver((entries) => {
      const e = entries[entries.length - 1];
      if (!e) return;
      const need = Math.min(this.stage.offsetHeight || 0, window.innerHeight || 0) * 0.35;
      this.sectionVisible = e.isIntersecting && e.intersectionRect.height >= need;
      this._sync();
    }, { threshold: [0, 0.05, 0.15, 0.25, 0.35, 0.5, 0.75, 1] });
    this._io.observe(this.stage);

    this._onVis = this._onVis || (() => this._sync());
    document.addEventListener('visibilitychange', this._onVis);

    this._onPageShow = this._onPageShow || ((e) => { if (e.persisted) this._sync(); });
    window.addEventListener('pageshow', this._onPageShow);

    // A click INSIDE a cross-origin iframe is invisible to us — except that the browser moves
    // focus to the iframe element and blurs the window. That is the only signal that an Instagram
    // post may have been started, and it is what decides whether leaving the card needs a fresh
    // frame (silence) or can simply keep the one it has (no reload).
    this._onWinBlur = this._onWinBlur || (() => {
      const el = document.activeElement;
      if (!el || el.tagName !== 'IFRAME') return;
      const c = this.cards.find((x) => x.iframe === el || x.el.contains(el));
      if (c && c.data.platform === 'instagram') c.igTouched = true;
    });
    window.addEventListener('blur', this._onWinBlur);

    this._onResize = this._onResize || (() => {
      if (this._rzRaf) return;
      this._rzRaf = requestAnimationFrame(() => { this._rzRaf = null; this._layout(); });
    });
    window.addEventListener('resize', this._onResize);

    this._onMsg = this._onMsg || ((e) => this._onTkMessage(e));
    window.addEventListener('message', this._onMsg);

    this._onWinUp = this._onWinUp || ((e) => this._swipeEnd(e));
    window.addEventListener('pointerup', this._onWinUp);

    if (this.designMode) {
      this._onBlockSelect = this._onBlockSelect || ((e) => {
        const idx = this.cards.findIndex((c) => c.el === e.target || c.el.contains(e.target));
        if (idx >= 0) { this._designOk = true; this._setActive(idx, true); }
      });
      document.addEventListener('shopify:block:select', this._onBlockSelect);
    }
  }

  _unbindWinDoc() {
    if (!this._winDocBound) return;
    this._winDocBound = false;
    if (this._io) { this._io.disconnect(); this._io = null; }
    if (this._onVis) document.removeEventListener('visibilitychange', this._onVis);
    if (this._onPageShow) window.removeEventListener('pageshow', this._onPageShow);
    if (this._onWinBlur) window.removeEventListener('blur', this._onWinBlur);
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    if (this._rzRaf) { cancelAnimationFrame(this._rzRaf); this._rzRaf = null; }
    if (this._onMsg) window.removeEventListener('message', this._onMsg);
    if (this._onWinUp) window.removeEventListener('pointerup', this._onWinUp);
    if (this._onBlockSelect) document.removeEventListener('shopify:block:select', this._onBlockSelect);
  }

  // ---------- element listeners: bound ONCE per element (editor moves keep them) ----------
  _bindEl() {
    if (this._boundEl) return;
    this._boundEl = true;

    this.addEventListener('click', (e) => {
      const t = e.target;
      if (!t || !t.closest) return;
      if (t.closest('[data-svv-fallback]')) return; // external link — let it navigate
      if (this._moved) return;                      // click synthesized after a swipe

      const prev = t.closest('[data-svv-prev]');
      if (prev) { if (prev.getAttribute('aria-disabled') !== 'true') this._go(-1); return; }
      const next = t.closest('[data-svv-next]');
      if (next) { if (next.getAttribute('aria-disabled') !== 'true') this._go(1); return; }

      const playBtn = t.closest('[data-svv-play]');
      if (playBtn) { this._togglePlay(this.cards[this.active], true); return; }

      const leaveBtn = t.closest('[data-svv-leave]');
      if (leaveBtn) {
        // their "View profile" button is unreachable inside the frame, so this hotspot is the tap
        e.preventDefault();
        e.stopPropagation();
        this._askLeave(leaveBtn.getAttribute('data-svv-href') || '');
        return;
      }
      const muteBtn = t.closest('[data-svv-mute]');
      if (muteBtn) { this._toggleMute(); return; }

      const big = t.closest('[data-svv-bigplay]');
      if (big) {
        const c = this.cards[this.active];
        c.userStarted = true;
        c.userPaused = false;
        this._hasGesture = true;
        if (big) big.hidden = true;
        this._playUpload(c, true);
        return;
      }

      if (t.closest('[data-svv-bar]')) return; // seek handled on pointer events

      const cardEl = t.closest('[data-svv-card]');
      if (cardEl) {
        const idx = this.cards.findIndex((c) => c.el === cardEl);
        if (idx < 0) return;
        if (idx !== this.active) { this._setActive(idx, true); return; }
        // tap the center card toggles play (uploads only; embeds keep their own surface)
        const c = this.cards[idx];
        if (c.data.kind === 'upload') this._togglePlay(c, true);
      }
    });

    // swipe: pointerdown on the stage; pointerup on window (gesture may end outside)
    this.stage.addEventListener('pointerdown', (e) => {
      if (this.cfg.swipe === false) return;
      if (e.button != null && e.button !== 0) return;
      // §hygiene: presses that start on interactive elements are not drags — clear the flag
      if (e.target.closest && e.target.closest('a,button,[data-svv-bar]')) { this._moved = false; return; }
      this._sx = e.clientX;
      this._sy = e.clientY;
      this._swiping = true;
    });

    // per-card: seek bar (pointer capture) + media element events
    this.cards.forEach((c) => {
      if (c.bar) {
        c.bar.addEventListener('pointerdown', (e) => {
          if (!c.video || !c.video.duration) return;
          c.bar.setPointerCapture(e.pointerId);
          c.seeking = true;
          this._seekTo(c, e.clientX);
        });
        c.bar.addEventListener('pointermove', (e) => { if (c.seeking) this._seekTo(c, e.clientX); });
        const endSeek = () => { c.seeking = false; };
        c.bar.addEventListener('pointerup', endSeek);
        c.bar.addEventListener('pointercancel', endSeek);
        c.bar.addEventListener('keydown', (e) => {
          if (!c.video || !c.video.duration) return;
          if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            e.preventDefault();
            const fwd = (e.key === 'ArrowRight') !== this.rtl; // reading-forward per direction
            c.video.currentTime = Math.max(0, Math.min(c.video.duration, c.video.currentTime + (fwd ? 5 : -5)));
          }
        });
      }
      this._bindMedia(c);
    });
  }

  _swipeEnd(e) {
    if (!this._swiping) return;
    this._swiping = false;
    const dx = e.clientX - this._sx;
    const dy = e.clientY - this._sy;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.4) {
      this._moved = true;
      const goNext = this.rtl ? dx > 0 : dx < 0; // reading-forward
      this._go(goNext ? 1 : -1);
      setTimeout(() => { this._moved = false; }, 0); // §hygiene: async-clear after pointerup
    }
  }

  // ---------- geometry ----------
  _rel(i) {
    let rel = i - this.active;
    if (this.cfg.loop !== false && this.n >= 4) {
      if (rel > this.n / 2) rel -= this.n;
      if (rel < -this.n / 2) rel += this.n;
    }
    return rel;
  }

  // A card can size ITSELF (an Instagram shell built around an uploaded clip emits its own
  // height), so the stage has to grow to the tallest card or the shell would be clipped.
  _fitStage() {
    if (!this.stage || !this.cards.length) return;
    let max = 0;
    for (const c of this.cards) max = Math.max(max, c.el.offsetHeight || 0);
    if (!max) return;
    // the breathing room is a merchant setting — read the SAME var the server paint used, or the
    // JS re-fit would silently restore the old hardcoded pad the moment a card measured itself
    const mobile = this.mobileq ? this.mobileq.matches : window.matchMedia('(max-width: 749px)').matches;
    const padVar = parseFloat(getComputedStyle(this).getPropertyValue(mobile ? '--svv-stage-pad-m' : '--svv-stage-pad-d'));
    const pad = Number.isFinite(padVar) ? padVar : (mobile ? 56 : 72);
    const want = Math.round(max + pad);
    if (this._stageH !== want) {
      this._stageH = want;
      this.stage.style.height = want + 'px';
    }
  }

  _layout() {
    if (!this.cards || !this.cards.length) return;
    const cs = getComputedStyle(this);
    const mobile = this.mobileq.matches;
    const first = this.cards[0].el;
    // ⚠️ never parseFloat the width var as a fallback any more — it is a CONTAINER unit now
    // ('60cqw'), so parseFloat would hand back 60 and lay the coverflow out on a 60px card
    const cw = first.offsetWidth || first.getBoundingClientRect().width || 300;
    const scale = parseFloat(cs.getPropertyValue('--svv-side-scale')) || 0.8;
    const gap = parseFloat(cs.getPropertyValue(mobile ? '--svv-gap-m' : '--svv-gap-d')) || 0;
    const offset = cw / 2 + (cw * scale) / 2 + gap;

    this._fitStage();
    this.cards.forEach((c, i) => {
      const rel = this._rel(i);
      const center = rel === 0;
      const far = Math.abs(rel) >= 2;
      const x = rel * offset * this.dirSign;
      c.el.style.transform = 'translate(-50%,-50%) translateX(' + x + 'px) scale(' + (center ? 1 : scale) + ')';
      c.el.style.zIndex = center ? 30 : String(20 - Math.abs(rel));
      c.el.style.opacity = far ? '0' : (center ? '1' : '0.55');
      c.el.style.pointerEvents = far ? 'none' : 'auto';
      c.el.classList.toggle('svv-card--center', center);
      // overlay: click-to-center on side cards; on the center card it is the tap surface for
      // uploads, but must NOT cover an Instagram embed (no API — its own UI is the only control)
      const igEmbed = c.data.kind === 'embed' && c.data.platform === 'instagram';
      if (c.ov) c.ov.style.pointerEvents = center && igEmbed ? 'none' : 'auto';
      this._syncControls(c);
      // a measured Instagram card re-solves its scale whenever the card box changes (breakpoint
      // switch, viewport resize) — the measurement itself stays valid, only the fit changes
      if (igEmbed && c.igLh) this._igApplyGeom(c);
      else this._syncBandScale(c);
    });

    // non-loop ends: aria-disabled (never `disabled` — §focus-drop convention)
    const looping = this.cfg.loop !== false && this.n >= 4;
    const prevBtn = this.querySelector('[data-svv-prev]');
    const nextBtn = this.querySelector('[data-svv-next]');
    if (prevBtn) prevBtn.setAttribute('aria-disabled', !looping && this.active === 0 ? 'true' : 'false');
    if (nextBtn) nextBtn.setAttribute('aria-disabled', !looping && this.active === this.n - 1 ? 'true' : 'false');
  }

  // ---------- the reducer ----------
  _autoplayAllowed() {
    if (this.cfg.autoplay === false) return false;
    if (this.rmq.matches || this.saveData) return false;
    // ⚠️ NOT gated on designMode any more. Suppressing autoplay until a block was selected meant a
    // merchant who ticked "Autoplay" watched the editor preview do nothing and reasonably reported
    // it as broken (owner 2026-08-10: "i need to click on the main video to run it even after i
    // enable Autoplay"). The editor must show what the storefront will do. Third-party EMBEDS are
    // still not loaded passively in the editor — that guard lives in _ensureLoaded, where it
    // belongs — so this only starts our own uploaded <video>.
    return true;
  }

  _sync() {
    if (!this.cards || !this.cards.length) return;
    const docVisible = !document.hidden;
    this.cards.forEach((c, i) => {
      const rel = this._rel(i);
      const center = rel === 0;
      const win = this._preloadFor(c); // how many neighbours this card is allowed to preload into
      const near = Math.abs(rel) <= win && !center;
      // HYSTERESIS: load at `win`, but only unload one step FURTHER out. Stepping back and forth
      // across the ring therefore never remounts anything — without this, every move tore down
      // the card that fell out of the window and re-fetched it on the way back, which reads as
      // "the whole slider reloads instead of moving" (owner-reported 2026-08-09).
      const keep = Math.abs(rel) <= win + 1;
      const kind = c.data.kind;

      // NOTHING loads until the section has actually been seen (or the visitor interacted):
      // off-screen sections must cost zero bytes, not even metadata.
      const mayLoad = this.sectionVisible || this._hasGesture;
      const igEmbed = kind === 'embed' && c.data.platform === 'instagram';
      if (igEmbed) {
        // ⚠️ An Instagram embed has NO player API and NO mute of any kind: a tap inside their
        // frame starts the video WITH SOUND (measured: muted=false, volume=1), and removing the
        // iframe is the ONLY stop button that exists.
        // A neighbour card is therefore MOUNTED but never played: Instagram paints its own cover
        // frame, likes and caption in it, which is what makes a side card show a real post
        // instead of a placeholder. The moment a card that WAS the live one stops being it, its
        // frame is replaced with a fresh one — that both silences anything the customer started
        // and leaves the post's cover on screen (we cannot preserve their playhead; no API).
        const alive = (center || near) && this.sectionVisible && docVisible;
        if (alive && mayLoad) {
          clearTimeout(c.igOffT); c.igOffT = null;
          if (!c.iframe) this._igMount(c);
          else if (!center && c.igTouched) {
            // ONLY a card the customer actually tapped can be playing (their embed never
            // autoplays), and only that one needs a fresh frame to go silent. Refreshing every
            // card you merely scrolled past is what made navigation look like a reload.
            c.igTouched = false;
            this._igRefresh(c);
          }
        } else if (c.iframe && !c.igOffT && (!keep || !this.sectionVisible || !docVisible)) {
          // short grace period: scrolling across the observer threshold must not thrash
          // mount/unmount (each mount is a fresh third-party load)
          c.igOffT = setTimeout(() => {
            c.igOffT = null;
            const idx = this.cards.indexOf(c);
            const stillWanted =
              Math.abs(this._rel(idx)) <= this._preloadFor(c) + 1 && this.sectionVisible && !document.hidden;
            if (!stillWanted) this._teardown(c);
          }, 400);
        }
        return;
      }
      // a hybrid card (Instagram header band + our uploaded video) carries a header-only
      // template: mount it with the same window rule, and never treat it as the player
      if (mayLoad && (center || near) && c.tpl && c.tpl.hasAttribute('data-svv-head-only')) {
        this._mountHeadBand(c);
      }
      if (center && mayLoad) {
        this._ensureLoaded(c);
      } else if (near && mayLoad && !this.saveData && !this.designMode) {
        if (kind === 'upload') this._warmUpload(c);
        else if (kind === 'tiktok' || (kind === 'embed' && c.data.platform === 'tiktok')) this._warmTk(c);
        // ig embeds are never warmed
      } else if (!keep) {
        this._teardown(c);
      }

      const want = center && this.sectionVisible && docVisible && !c.userPaused &&
        (this._autoplayAllowed() || c.userStarted);
      this._applyWant(c, want, center);

      // ⚠️ A refused autoplay must never look like a broken card. When the POLICY gate says no
      // (prefers-reduced-motion / Data Saver / autoplay off) we never call play(), so nothing
      // rejects and _needsGesture — the only thing that paints the play button — was unreachable:
      // the visitor saw a still frame whose only control was a sound toggle for a video that was
      // not playing. The button is now the resting state of "centre, loaded, not playing".
      if (center && this.sectionVisible && docVisible && !c.userPaused && !c.everPlayed &&
          kind === 'upload' && c.video && !this._autoplayAllowed()) {
        this._needsGesture(c);
      }
    });
  }

  _applyWant(c, play, center) {
    const kind = c.data.kind;
    if (kind === 'upload') {
      if (!c.video) return;
      if (play) {
        if (c.video.paused) this._playUpload(c, false);
      } else if (!c.video.paused) {
        c.video.pause();
      }
      if (!center) {
        // leaving center: facade back on so the next reveal is clean
        c.userPaused = false;
        c.userStarted = false;
      }
    } else if (kind === 'embed' && c.data.platform === 'tiktok') {
      c.want = play ? 'play' : 'pause';
      if (c.iframe && c.ready) {
        if (play) this._tkFocusPlay(c);
        else { this._tkCmd(c, 'pause'); c.paused = true; c.el.classList.remove('svv-card--playing'); this._syncControls(c); }
      }
      if (!center) { c.userPaused = false; c.userStarted = false; }
    }
    // ig embed: no API — activation/teardown IS the playback control
  }

  // ---------- upload adapter ----------
  _pickSource(c) {
    if (c.pick) return c.pick;
    const sources = (c.data.sources || []).filter((s) => s && s.url);
    if (!sources.length) return null;
    const probe = document.createElement('video');
    const hls = sources.find((s) => s.format === 'm3u8' || /mpegurl/i.test(s.mime || ''));
    if (hls && probe.canPlayType('application/vnd.apple.mpegurl') !== '') {
      c.pick = hls;
      return c.pick;
    }
    const mp4s = sources.filter((s) => s.format !== 'm3u8' && !/mpegurl/i.test(s.mime || ''))
      .sort((a, b) => (a.height || 0) - (b.height || 0));
    // ⚠️ uncapped, need = cardHeight × dpr ≈ 1300+ on any modern phone — which EXCEEDS 1080, so
    // the find() below always failed and every no-HLS browser (real Android Chrome, the IG
    // WebView) took the largest file. Cap what a phone can ever "need" at 720 rows: today's
    // 1080p-only ladders still resolve to the same file, but the moment a smaller rendition
    // exists it becomes choosable — the content fix takes effect with no code change.
    let need = (c.el.offsetHeight || 500) * Math.min(window.devicePixelRatio || 1, 2);
    if (window.matchMedia('(max-width: 749px)').matches) need = Math.min(need, 720);
    c.pick = mp4s.find((s) => (s.height || 0) >= need) || mp4s[mp4s.length - 1] || sources[0];
    return c.pick;
  }

  _bindMedia(c) {
    if (!c.video || c.mediaBound) return;
    c.mediaBound = true;
    const v = c.video;
    v.addEventListener('playing', () => {
      clearTimeout(c.stallT); // ⚠️ a pending stall timer would fire DURING playback and pin the
      c.stallT = null;        //    spinner over a running video (owner-reported 2026-08-09)
      clearTimeout(c.startT); c.startT = null; // real frames are moving: the start deadline is moot
      c.everPlayed = true;
      this._syncControls(c);
      c.el.classList.add('svv-card--playing');
      c.el.classList.add('svv-card--facade-off');
      if (c.spinner) c.spinner.hidden = true;
      if (c.bigplay) c.bigplay.hidden = true;
      this._startProgress(c);
    });
    v.addEventListener('pause', () => {
      clearTimeout(c.startT); c.startT = null;
      c.el.classList.remove('svv-card--playing');
      this._stopProgress();
      this._syncControls(c); // a real pause is what reveals the progress row
    });
    v.addEventListener('loadedmetadata', () => {
      if (c.durEl && v.duration) c.durEl.textContent = this._fmt(v.duration);
      if (!c.sought && c.data.start > 0 && v.duration > c.data.start) {
        c.sought = true;
        try { v.currentTime = c.data.start; } catch (e) {}
      }
    });
    v.addEventListener('waiting', () => {
      clearTimeout(c.stallT);
      // only a REAL stall: readyState < HAVE_FUTURE_DATA. A loop restart fires `waiting` too.
      c.stallT = setTimeout(() => {
        if (c.spinner && !v.paused && v.readyState < 3) c.spinner.hidden = false;
      }, 1500);
    });
    v.addEventListener('ended', () => {
      clearTimeout(c.stallT);
      clearTimeout(c.startT); c.startT = null;
      this._syncControls(c);
      c.el.classList.remove('svv-card--playing');
      this._stopProgress();
      if (this.cfg.advanceOnEnd && this.cards[this.active] === c) this._go(1, false);
    });
    v.addEventListener('error', () => {
      // HLS pick failed -> silently fall back to the best mp4 once; mp4 failed -> poster + retry-on-tap
      if (c.pick && (c.pick.format === 'm3u8' || /mpegurl/i.test(c.pick.mime || '')) && !c.hlsFailed) {
        c.hlsFailed = true;
        c.pick = null;
        const mp4s = (c.data.sources || []).filter((s) => s.format !== 'm3u8');
        if (mp4s.length) {
          c.pick = mp4s[mp4s.length - 1];
          v.src = c.pick.url;
          if (this.cards[this.active] === c) this._playUpload(c, false);
          return;
        }
      }
      c.el.classList.remove('svv-card--facade-off', 'svv-card--playing');
      if (c.spinner) c.spinner.hidden = true;
      if (c.bigplay) c.bigplay.hidden = false; // tap = retry
      v.removeAttribute('src');
    });
  }

  _warmUpload(c) {
    if (!c.video || c.video.getAttribute('src')) return;
    // Warming a NEIGHBOUR is a nicety (real first frame instead of the cover). The centre card
    // never passes through here with a bare src for playback — _playUpload calls us but then
    // plays regardless — so gating on an expensive connection only costs the side cards their
    // early frame, never playback. Measured: 3 cards × 8-14MB fetching concurrently on cellular.
    const conn = navigator.connection || {};
    const isCentre = this.cards[this.active] === c;
    if (!isCentre && (conn.saveData || /(^|-)2g$|^3g$/.test(conn.effectiveType || ''))) return;
    const pick = this._pickSource(c);
    if (!pick) return;
    c.video.preload = 'metadata';
    // as soon as the first frame is decodable, show it: a neighbour card should display real
    // video, not its cover image (and a card the customer already watched keeps the frame it was
    // paused on, because pausing never restores the facade)
    c.video.addEventListener(
      'loadeddata',
      () => {
        // reveal the real first frame instead of the poster — UNLESS the merchant uploaded a
        // cover image on purpose, in which case their cover must stay until playback starts
        // (owner-reported: "Cover image not working with an uploaded video")
        if (c.el.hasAttribute('data-svv-has-cover')) return;
        if (c.video && c.video.getAttribute('src')) c.el.classList.add('svv-card--facade-off');
      },
      { once: true }
    );
    c.video.src = pick.url;
  }

  _playUpload(c, gestureBased) {
    if (!c.video) return;
    this._warmUpload(c);
    // "Start with sound" is legal ONLY for playback the visitor started: their tap is the user
    // activation browsers demand. Autoplay can never carry sound, so the grant needs gestureBased
    // — and it is never taken behind an explicit mute. We never arm a later gesture to sneak the
    // sound on afterwards (owner's standing policy: if the browser says no, take the no).
    const grant = gestureBased && this.cfg.soundOnStart === true && !this._mutedByUser;
    const v = c.video;
    // ⚠️ `this.muted` is SECTION-WIDE. Writing it here would hand the sound to cards the visitor
    // never touched — the next card would AUTOPLAY unmuted, which is precisely what the policy
    // forbids. So the grant lives only in this local until play() actually resolves, and it is
    // recorded as `_soundFromPlayTap` so a card change can hand it back.
    const wantUnmuted = grant || (!this.muted && (gestureBased || this._hasGesture));
    v.muted = !wantUnmuted;
    v.defaultMuted = v.muted;
    const p = v.play();
    if (p && p.catch) {
      p.then(() => {
        if (wantUnmuted) {
          this.muted = false;
          if (grant) this._soundFromPlayTap = true; // per-card permission, not a section-wide unmute
        }
        this._syncIcons();
        // ⚠️ play() RESOLVING does not mean pixels are moving. On a slow connection it resolves
        // and then buffers for tens of seconds — the promise never rejects, so the rejection path
        // never runs and the visitor waits at a frozen frame with no control. Give up after 8s
        // and hand them the play button. Cleared by the 'playing'/'ended' handlers.
        clearTimeout(c.startT);
        c.startT = setTimeout(() => {
          if (!c.video || c.video.paused) return;
          if (c.video.readyState >= 3 && c.video.currentTime > 0) return;
          if (c.spinner) c.spinner.hidden = true;
          this._needsGesture(c);
        }, 8000);
      }).catch(() => {
        if (wantUnmuted) {
          // unmuted start rejected — play muted instead and make the icon tell the truth
          this.muted = true;
          this._soundFromPlayTap = false;
          v.muted = v.defaultMuted = true;
          this._syncIcons();
          // ⚠️ a pause landing while play() was pending rejects it with AbortError — retrying
          // blindly would restart a video the visitor just stopped
          if (c.userPaused || this.cards[this.active] !== c || document.hidden || !this.sectionVisible) return;
          const p2 = v.play();
          if (p2 && p2.catch) p2.catch(() => this._needsGesture(c));
        } else {
          this._needsGesture(c);
        }
      });
    }
  }

  _needsGesture(c) {
    // autoplay rejected (iOS Low-Power etc.): poster + big play button; no retry loop
    c.el.classList.remove('svv-card--playing', 'svv-card--facade-off');
    if (c.spinner) c.spinner.hidden = true;
    if (c.bigplay) c.bigplay.hidden = false;
  }

  _togglePlay(c, gestureBased) {
    if (!c) return;
    if (c.data.kind === 'upload') {
      if (!c.video) return;
      if (c.video.paused) {
        c.userPaused = false;
        c.userStarted = true;
        if (gestureBased) this._hasGesture = true;
        this._playUpload(c, gestureBased);
      } else {
        c.userPaused = true;
        c.video.pause();
      }
    } else if (c.data.kind === 'embed' && c.data.platform === 'tiktok' && c.ready) {
      if (c.paused) {
        c.userPaused = false; c.userStarted = true;
        if (gestureBased) this._hasGesture = true;
        // ⚠️ deliberately NO sound grant here: TikTok's player API returns no acknowledgement of
        // the mute state, so a refusal would leave a permanent "sound on" icon over a silent
        // video. sound_on_start is uploads-only, where the play() promise gives a real yes/no.
        this._tkFocusPlay(c);
      }
      else { c.userPaused = true; this._tkCmd(c, 'pause'); c.paused = true; c.el.classList.remove('svv-card--playing'); }
    }
  }

  _toggleMute() {
    this._hasGesture = true;
    this.muted = !this.muted;
    // remember an explicit silence: soundOnStart must never override the visitor's own choice
    this._mutedByUser = this.muted;
    this._soundFromPlayTap = false; // an explicit press outranks (and outlives) a play-tap grant
    this._syncIcons();
    const c = this.cards[this.active];
    if (!c) return;
    if (c.data.kind === 'upload' && c.video) {
      c.video.muted = c.video.defaultMuted = this.muted;
      if (!this.muted && c.video.paused && !c.userPaused) this._playUpload(c, true);
    } else if (c.data.kind === 'embed' && c.data.platform === 'tiktok' && c.ready) {
      this._tkCmd(c, this.muted ? 'mute' : 'unMute');
    }
  }

  _syncIcons() {
    this.cards.forEach((c) => {
      c.el.classList.toggle('svv-card--muted', this.muted);
      c.el.classList.toggle('svv-card--sound-on', !this.muted);
    });
  }

  // The controls row (progress + time + play) is NOT chrome that sits on a playing video: it
  // appears only once the customer has actually PAUSED a video she started — never at the start,
  // never during playback (owner 2026-08-09). The sound button lives outside it and stays
  // available while the video plays.
  _syncControls(c) {
    const center = this.cards[this.active] === c;
    const upload = c.data.kind === 'upload';
    const paused = upload ? !!(c.video && c.video.paused) : !!c.paused;
    const showRow = !!(center && c.everPlayed && paused);
    if (c.controls) c.controls.hidden = !showRow;
    // the floating sound chip yields to the row's own button, so the cluster reads as one group
    // ...and it must not sit alone on a still frame: until the video has actually played, the
    // play button is the only affordance the card should offer
    if (c.sound) c.sound.hidden = showRow || !(center && (upload || c.ready)) || (upload && !c.everPlayed && paused);
  }

  // ---------- progress (one rAF per section, only while the center upload plays) ----------
  _startProgress(c) {
    this._stopProgress();
    const v = c.video;
    if (!v || !c.fill) return;
    let lastSec = -1;
    const tick = () => {
      if (!v.duration || v.paused) { this._progRaf = null; return; }
      const frac = v.currentTime / v.duration;
      if (!c.seeking) c.fill.style.transform = 'scaleX(' + frac.toFixed(4) + ')';
      if (c.bar) c.bar.setAttribute('aria-valuenow', String(Math.round(frac * 100)));
      const sec = Math.floor(v.currentTime);
      if (sec !== lastSec) {
        lastSec = sec;
        if (c.curEl) c.curEl.textContent = this._fmt(v.currentTime);
      }
      this._progRaf = requestAnimationFrame(tick);
    };
    this._progRaf = requestAnimationFrame(tick);
  }

  _stopProgress() {
    if (this._progRaf) { cancelAnimationFrame(this._progRaf); this._progRaf = null; }
  }

  _seekTo(c, clientX) {
    const v = c.video;
    if (!v || !v.duration || !c.bar) return;
    const r = c.bar.getBoundingClientRect();
    let frac = this.rtl ? (r.right - clientX) / r.width : (clientX - r.left) / r.width;
    frac = Math.max(0, Math.min(1, frac));
    try { v.currentTime = frac * v.duration; } catch (e) {}
    if (c.fill) c.fill.style.transform = 'scaleX(' + frac.toFixed(4) + ')';
  }

  _fmt(t) {
    t = Math.max(0, Math.floor(t || 0));
    const m = Math.floor(t / 60);
    const s = t % 60;
    return m + ':' + (s < 10 ? '0' : '') + s;
  }

  // ---------- embeds ----------
  _ensureLoaded(c) {
    const kind = c.data.kind;
    if (kind === 'upload') { this._warmUpload(c); return; }
    if (this.designMode && !this._designOk) return; // never load embeds passively in the editor
    if (c.data.platform === 'tiktok') this._tkEnqueue(c);
    else this._igMount(c);
  }

  // how many cards either side of the centre this card may preload into: the section value,
  // overridden per card ('on' forces at least one, 'off' means this card only loads when centred)
  _preloadFor(c) {
    let base = this.cfg.preload == null ? 1 : parseInt(this.cfg.preload, 10);
    if (!(base >= 0)) base = 1;
    base = Math.min(base, 15); // hard ceiling mirrors the schema max — a runaway value can't mount hundreds of frames
    const own = c.data && c.data.preload;
    if (own === 'off') return 0;
    if (own === 'on') return Math.max(1, base);
    return base;
  }

  // swap an Instagram frame for a fresh one: stops whatever the customer started (their embed has
  // no pause) and immediately puts the post's own cover back on the card
  _igRefresh(c) {
    if (!c.iframe) return;
    this._removeIframe(c);
    this._igMount(c);
  }

  // header-only band for a hybrid card: decorative, mounted once, torn down with the card.
  // Deliberately does NOT set c.iframe — the player is the <video>, and c.iframe drives the
  // embed state machine.
  // ⚠️ NEVER window.open(url, '_blank', 'noopener') for an external link on mobile: Chrome creates
  // the tab, the URL is then claimed by the target app (Instagram), and the tab it made is stranded
  // on about:blank — which is what the visitor finds when they switch back (owner 2026-08-10).
  // A real anchor navigation hands off cleanly. Used only for the fallback paths; the dialog's
  // confirm control is itself an <a>, so the visitor's own click drives that one.
  _openExternal(href) {
    if (!href) return;
    const a = document.createElement('a');
    a.href = href;
    a.target = '_blank';
    a.rel = 'noopener nofollow';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  _askLeave(href) {
    // the dialog is rendered by the SECTION, which may sit outside this custom element — look in
    // the section wrapper before giving up (a null lookup here silently skipped the confirmation
    // and opened the tab straight away)
    const scope = this.closest('.shopify-section') || document;
    const dlg = this.querySelector('[data-svv-leave-dialog]') || scope.querySelector('[data-svv-leave-dialog]');
    if (!dlg || !href) {
      this._openExternal(href);
      return;
    }
    this._leaveHref = href;
    if (!this._leaveBound) {
      this._leaveBound = true;
      const go = dlg.querySelector('[data-svv-leave-go]');
      if (go) go.addEventListener('click', (ev) => {
        const url = this._leaveHref;
        if (go.tagName === 'A') {
          // let the browser perform the navigation itself — closing the dialog synchronously here
          // would tear the element out from under its own default action, so defer it
          if (!url) { ev.preventDefault(); return; }
          setTimeout(() => { try { dlg.close(); } catch (err) {} }, 0);
          return;
        }
        try { dlg.close(); } catch (err) {}
        this._openExternal(url);
      });
      // every "stay here" control — the cancel button AND the close X — carries the same hook,
      // so this must be querySelectorAll: a querySelector would leave the X inert
      dlg.querySelectorAll('[data-svv-leave-no]').forEach((el) => {
        el.addEventListener('click', () => { try { dlg.close(); } catch (err) {} });
      });
      // click outside the panel closes it (the dialog element itself is the backdrop surface)
      dlg.addEventListener('click', (ev) => { if (ev.target === dlg) { try { dlg.close(); } catch (err) {} } });
    }
    // point the confirm link at THIS card's destination before the sheet opens
    const goA = dlg.querySelector('[data-svv-leave-go]');
    if (goA && goA.tagName === 'A') goA.setAttribute('href', href);
    try { dlg.showModal(); } catch (err) { this._openExternal(href); }
  }

  _mountHeadBand(c) {
    if (!c.tpl || c.headMounted) return;
    const host = c.el.querySelector('.svv-card__frame');
    if (!host) return;
    c.headMounted = true;
    host.appendChild(c.tpl.content.cloneNode(true));
  }

  _mountIframe(c, cacheBust) {
    if (!c.tpl || c.iframe) return null;
    this._clearFrames(c); // never stack frames on a re-mount
    const frag = c.tpl.content.cloneNode(true);
    const iframe = frag.querySelector('iframe');
    if (!iframe) return null;
    if (cacheBust) {
      const join = iframe.src.indexOf('?') >= 0 ? '&' : '?';
      iframe.src = iframe.src + join + '_r=' + c.tries;
    }
    c.el.querySelector('.svv-card__frame').appendChild(frag);
    // composite cards carry a decorative header iframe too — the BODY one is the real player
    c.iframe = c.el.querySelector('iframe:not([data-svv-head])') || c.el.querySelector('iframe');
    if (c.spinner) c.spinner.hidden = false;
    return c.iframe;
  }

  // TikTok: serialized loader (at most ONE iframe loading at any moment — replaces the
  // reference-era timer stagger with event-driven back-pressure) + ready watchdog.
  _tkEnqueue(c) {
    if (c.iframe || c.failed) return;
    if (this._tkQueue.indexOf(c) >= 0 || this._tkLoading === c) return;
    // the center card jumps the queue
    if (this.cards[this.active] === c) this._tkQueue.unshift(c);
    else this._tkQueue.push(c);
    this._tkProcess();
  }

  _warmTk(c) { this._tkEnqueue(c); }

  _tkProcess() {
    if (this._tkLoading || !this._tkQueue.length) return;
    const c = this._tkQueue.shift();
    if (!c || c.iframe || c.failed) { this._tkProcess(); return; }
    this._tkLoading = c;
    const token = ++c.token;
    this._mountIframe(c, c.tries > 0);
    clearTimeout(c.watchdog);
    c.watchdog = setTimeout(() => {
      if (c.token !== token || c.ready) return;
      this._removeIframe(c);
      if (c.tries < 3) {
        c.tries++;
        this._tkLoading = null;
        this._tkEnqueue(c);
      } else {
        c.failed = true;
        if (c.spinner) c.spinner.hidden = true;
        if (c.fallback) c.fallback.hidden = false;
        this._tkLoading = null;
        this._tkProcess();
      }
    }, 8000);
  }

  _tkCmd(c, type, value) {
    if (!c.iframe || !c.iframe.contentWindow) return;
    const msg = { type: type, 'x-tiktok-player': true };
    if (value !== undefined) msg.value = value;
    c.iframe.contentWindow.postMessage(msg, '*');
  }

  _tkFocusPlay(c) {
    c.el.classList.add('svv-card--playing'); // icon state (uploads get it from media events)
    this._tkCmd(c, this.muted ? 'mute' : 'unMute');
    if (c.data.start > 0 && !c.sought) { c.sought = true; this._tkCmd(c, 'seekTo', c.data.start); }
    this._tkCmd(c, 'play');
    c.paused = false;
    // the first play can land mid-seek/buffer and be dropped — one retry, only if still center
    clearTimeout(c.replayT);
    c.replayT = setTimeout(() => {
      if (this.cards[this.active] === c && !c.paused && !c.userPaused) this._tkCmd(c, 'play');
    }, 280);
  }

  _onTkMessage(e) {
    let d = e.data;
    if (typeof d === 'string') { try { d = JSON.parse(d); } catch (err) { return; } }
    if (!d) return;
    // Instagram's embed broadcasts its REAL rendered height to the parent ({type:'MEASURE'}).
    // Liquid can only estimate that height (the caption block is the variable part), so the card
    // re-solves its own geometry from the measurement — nothing of the post can be cut, whatever
    // the caption length, and in auto mode the layout width is refined once so the post fills the
    // card exactly (which is also what gives Instagram's username column its width).
    if (d.type === 'MEASURE' && d.details && d.details.height) { this._igMeasure(e.source, d.details.height); return; }
    if (d['x-tiktok-player'] !== true) return;
    const c = this.cards.find((x) => x.iframe && x.iframe.contentWindow === e.source);
    if (!c) return;
    if (d.type === 'onPlayerReady') {
      c.ready = true;
      this._syncControls(c);
      clearTimeout(c.watchdog);
      if (c.spinner) c.spinner.hidden = true;
      if (this._tkLoading === c) { this._tkLoading = null; this._tkProcess(); }
      if (c.want === 'play') {
        c.el.classList.add('svv-card--facade-off');
        c.everPlayed = true;
        this._tkFocusPlay(c);
      } else {
        this._tkCmd(c, 'pause');
        c.paused = true;
        // facade stays ON over paused warm frames — no half-loaded flash
      }
      return;
    }
    if (c.want === 'play' && !c.el.classList.contains('svv-card--facade-off')) {
      c.el.classList.add('svv-card--facade-off');
    }
    if (c.want === 'pause' && (d.type === 'onCurrentTime' || d.type === 'onStateChange')) {
      this._tkCmd(c, 'pause');
    }
  }

  // Instagram: mount-on-activation only. There is NO player API — activation and teardown
  // ARE the playback control; the platform's own in-frame UI does the rest.
  _igMount(c) {
    if (c.iframe || c.failed) return;
    const token = ++c.token;
    const iframe = this._mountIframe(c, false);
    if (!iframe) return;
    iframe.addEventListener('load', () => {
      if (c.token !== token) return;
      clearTimeout(c.watchdog);
      setTimeout(() => {
        if (c.token !== token) return;
        if (c.spinner) c.spinner.hidden = true;
        c.el.classList.add('svv-card--facade-off');
      }, 300);
    }, { once: true });
    clearTimeout(c.watchdog);
    c.watchdog = setTimeout(() => {
      if (c.token !== token || !c.iframe) return;
      if (c.spinner) c.spinner.hidden = true;
      if (c.fallback) c.fallback.hidden = false;
    }, 8000);
  }

  _igMeasure(src, h) {
    if (!(h > 200) || h > 4000) return;
    const c = this.cards.find((x) => x.iframe && x.iframe.contentWindow === src);
    if (!c || c.data.platform !== 'instagram' || c.data.kind !== 'embed') return;
    const cs = getComputedStyle(c.el);
    const num = (n) => parseFloat(cs.getPropertyValue(n)) || 0;
    const hdr = num('--svv-ig-hdr');
    const mr = (num('--svv-ig-mr') || 125) / 100;
    let lw = num('--svv-ig-lw');
    if (!(lw > 0) || !(hdr > 0)) return;
    // ⚠️ offsetWidth/Height, NOT getBoundingClientRect: a side card carries the coverflow's
    // transform: scale(), and a scaled rect would make us solve the geometry for the shrunken
    // box and then shrink it AGAIN — the post stopped filling every non-centre card.
    const W = c.el.offsetWidth, H = this._cardSolveHeight(c);
    if (!(W > 0) || !(H > 0)) return;
    // ONE refinement pass, auto mode only: with the true caption height known, pick the layout
    // width that makes the post fill the card. The frame re-lays out and measures again, and the
    // next message applies the final scale. Guarded so it can never ping-pong.
    if (c.el.hasAttribute('data-svv-ig-auto') && !c.igRefined) {
      c.igRefined = true;
      const foot = h - hdr - lw * mr;
      const denom = H - W * mr;
      if (foot > 0 && denom > 0) {
        const want = Math.round((W * (hdr + foot)) / denom);
        if (want >= 260 && want <= 900 && Math.abs(want - lw) > 8) {
          c.el.style.setProperty('--svv-ig-lw', want + 'px');
          return;
        }
      }
    }
    c.igLh = h;
    this._igApplyGeom(c);
  }

  // The card can be trimmed at the bottom, which CROPS it — so the Instagram post must still be
  // solved for the card's full (untrimmed) height, otherwise trimming would shrink the whole post
  // instead of cutting its bottom off.
  _cardSolveHeight(c) {
    const trim = (window.matchMedia('(max-width: 749px)').matches ? this.cfg.trimMobile : this.cfg.trim) || 0;
    return c.el.offsetHeight + (trim > 0 ? trim : 0);
  }

  // The card's width is a PERCENTAGE of the section, so its px value is unknown to Liquid — but
  // Instagram's layout width is an absolute px (it decides how much of a username prints), so the
  // scale between them can only be solved from the measured box. Every other band value is a pure
  // ratio of the card width and is already exact in CSS; this is the one CSS cannot express
  // (a unitless length / length). Hybrid shell cards never reach _igApplyGeom — they have no
  // MEASURE message — so without this their strips would keep the server's estimate.
  _syncBandScale(c) {
    if (!c || !c.el || c.data.platform !== 'instagram') return;
    const cs = getComputedStyle(c.el);
    const lw = parseFloat(cs.getPropertyValue('--svv-ig-lw'));
    const W = c.el.offsetWidth; // layout box: never the coverflow-scaled rect
    if (!(lw > 0) || !(W > 0)) return;
    const k = Math.round((W / lw) * 1000);
    if (c._bandK === k) return; // avoid style churn on every layout pass
    c._bandK = k;
    c.el.style.setProperty('--svv-ig-k', k);
    c.el.style.setProperty('--svv-ig-k-m', k);
  }

  _igApplyGeom(c) {
    if (!c.igLh) return;
    const cs = getComputedStyle(c.el);
    const num = (n) => parseFloat(cs.getPropertyValue(n)) || 0;
    const hdr = num('--svv-ig-hdr');
    const mr = (num('--svv-ig-mr') || 125) / 100;
    const lw = num('--svv-ig-lw');
    const lh = c.igLh;
    const W = c.el.offsetWidth, H = this._cardSolveHeight(c); // layout box: immune to the card's scale()
    if (!(lw > 0) || !(lh > 0) || !(W > 0) || !(H > 0)) return;
    const k = Math.min(W / lw, H / lh);
    const dx = Math.round((W - lw * k) / 2);
    const dy = Math.round((H - lh * k) / 2);
    const h12 = Math.round(hdr * k);
    const vt = dy + h12;
    const vb = dy + Math.round((hdr + lw * mr) * k);
    const set = (n, v) => { c.el.style.setProperty(n, v); c.el.style.setProperty(n + '-m', v); };
    set('--svv-ig-lh', Math.round(lh) + 'px');
    set('--svv-ig-k', String(Math.round(k * 1000)));
    set('--svv-ig-dx', dx + 'px');
    set('--svv-ig-dy', dy + 'px');
    set('--svv-ig-h12', h12 + 'px');
    set('--svv-ig-vh', Math.round(lh * k) + 'px');
    set('--svv-ig-vt', vt + 'px');
    set('--svv-ig-vb', vb + 'px');
    // pillarbox covers: rendered frame width and the strip width follow the same scale
    const fw = Math.round(lw * k);
    const barPx = Math.max(0, Math.round(((lw - (lw * mr * 9) / 16) / 2) * k));
    set('--svv-ig-fw', fw + 'px');
    set('--svv-ig-bar', barPx + 'px');
  }

  // ⚠️ A composite (split-header) card mounts TWO frames wrapped in band spans, so tearing down
  // `c.iframe` alone left the decorative frame loaded — and the next mount cloned the template on
  // top of the leftovers, stacking a third frame. Always clear everything the template produced.
  _clearFrames(c) {
    c.headMounted = false;
    const host = c.el.querySelector('.svv-card__frame');
    if (host) {
      host.querySelectorAll('.svv-card__igband, iframe').forEach((n) => {
        if (n.parentNode) n.parentNode.removeChild(n);
      });
    } else if (c.iframe && c.iframe.parentNode) {
      c.iframe.parentNode.removeChild(c.iframe);
    }
  }

  _removeIframe(c) {
    this._clearFrames(c);
    c.iframe = null;
    c.ready = false;
  }

  _teardown(c) {
    c.token++;
    clearTimeout(c.igOffT); c.igOffT = null;
    clearTimeout(c.watchdog);
    clearTimeout(c.replayT);
    clearTimeout(c.stallT);
    clearTimeout(c.startT); c.startT = null;
    if (c.data.kind === 'upload') {
      if (c.video && c.video.getAttribute('src')) {
        try { c.video.pause(); } catch (e) {}
        c.video.removeAttribute('src');
        try { c.video.load(); } catch (e) {}
      }
      // ⚠️ hybrids kept their band iframes FOREVER — cycling 6 cards left 12 live cross-origin
      // frames (~6-12 renderer processes on cheap Androids). Release them with the same window
      // the embeds use; _mountHeadBand re-mounts on re-entry (headMounted resets in _clearFrames).
      // Trade-off, stated: a card 3+ steps away visibly reloads its bands on return.
      this._clearFrames(c);
    } else {
      this._removeIframe(c);
      if (this._tkLoading === c) { this._tkLoading = null; this._tkProcess(); }
      const qi = this._tkQueue.indexOf(c);
      if (qi >= 0) this._tkQueue.splice(qi, 1);
    }
    c.el.classList.remove('svv-card--facade-off', 'svv-card--playing');
    if (c.spinner) c.spinner.hidden = true;
    if (c.bigplay) c.bigplay.hidden = true;
    c.userPaused = false;
    c.userStarted = false;
    c.sought = false;
    c.paused = true;
    c.everPlayed = false;
    if (c.controls) c.controls.hidden = true;
    if (c.sound) c.sound.hidden = true;
  }

  // ---------- navigation ----------
  _go(dir, gestureBased) {
    const looping = this.cfg.loop !== false && this.n >= 4;
    let next = this.active + dir;
    if (looping) next = (next + this.n) % this.n;
    else next = Math.max(0, Math.min(this.n - 1, next));
    if (next === this.active) return;
    this._setActive(next, gestureBased !== false);
  }

  _setActive(i, gestureBased) {
    // a play-tap granted sound for THAT card only — every card change hands it back, so nothing
    // the visitor did not tap can ever start with sound. (_setActive is the single funnel for
    // arrows, swipe, card tap and shopify:block:select, incl. the non-gesture auto-advance.)
    if (this._soundFromPlayTap) { this.muted = true; this._soundFromPlayTap = false; }
    if (gestureBased) {
      this._hasGesture = true;
      if (this.mobileq.matches) {
        // mobile: every card starts muted, like a real feed (also keeps autoplay legal)
        this.muted = true;
      }
    }
    this.active = i;
    const c = this.cards[i];
    if (c) { c.userPaused = false; }
    this._syncIcons();
    this._layout();
    this._sync();
    this._announce();
  }

  _announce() {
    const st = this.querySelector('[data-svv-status]');
    if (st) st.textContent = 'סרטון ' + (this.active + 1) + ' מתוך ' + this.n;
  }
}

if (!customElements.get('social-video-slider')) {
  customElements.define('social-video-slider', SocialVideoSlider);
}
